import { useState, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import { ReactComponent as PlasticBinIcon } from "../../../static/assets/Plastic-bin.svg";
import { ReactComponent as OpenInNewIcon } from "../../../static/assets/open_in_new.svg";
import { ReactComponent as ImageIcon } from "../../../static/assets/image.svg";
import { ReactComponent as IconBiowaste } from "../../../static/assets/biowaste.svg";
import { ReactComponent as IconWood } from "../../../static/assets/wood.svg";
import { ReactComponent as IconMineral } from "../../../static/assets/mineral.svg";
import { ReactComponent as IconMetal } from "../../../static/assets/metal.svg";
import { ReactComponent as IconCardboard } from "../../../static/assets/cardboard.svg";
import { ReactComponent as IconPlastic } from "../../../static/assets/plastic.svg";
import { ReactComponent as IconPlaster } from "../../../static/assets/plaster.svg";
import { ReactComponent as IconTextil } from "../../../static/assets/textil.svg";
import { ReactComponent as IconGlass } from "../../../static/assets/glass.svg";
import { ReactComponent as IconAuther } from "../../../static/assets/auther.svg";

const PARENT_CATEGORY_ORDER = {
  wood: 0,
  biowaste: 1,
  cardboard: 2,
  metal: 3,
  mineral: 4,
  plastic: 5,
  plaster: 6,
  textile: 7,
  glass: 8,
  other: 9,
};

const DEFAULT_CATEGORY_COLOR = "#6366f1";

/** Composant SVG par catégorie parente (assets/material categories). */
const CATEGORY_ICON_COMPONENTS = {
  biowaste: IconBiowaste,
  wood: IconWood,
  mineral: IconMineral,
  metal: IconMetal,
  cardboard: IconCardboard,
  plastic: IconPlastic,
  plaster: IconPlaster,
  textile: IconTextil,
  glass: IconGlass,
  other: IconAuther,
};

function getCategoryIconComponent(parentCategory) {
  return CATEGORY_ICON_COMPONENTS[parentCategory] || IconAuther;
}

function getMaterialsFromStorage() {
  try {
    const raw = localStorage.getItem("materialsDescription");
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

/**
 * Matières non autorisées pour le flux, groupées par parentCategory.
 */
function buildUnauthorizedGroups(container) {
  const authorized = container?.contentCategoryJoin?.authorizedMaterial || [];
  const authorizedSet = new Set(
    authorized
      .map((m) => (m?.value ?? m?.referenceName ?? "").toString().toLowerCase())
      .filter(Boolean),
  );

  const allMaterials = getMaterialsFromStorage();
  const sortingErrorMaterials = (allMaterials || []).filter(
    (m) => m?.isActiveSortingError === true,
  );
  const unauthorized = sortingErrorMaterials.filter(
    (m) =>
      !authorizedSet.has((m?.referenceName ?? "").toString().toLowerCase()),
  );

  const byCategory = {};
  for (const m of unauthorized) {
    const key = m?.parentCategory ?? "other";
    if (!byCategory[key])
      byCategory[key] = {
        color: m?.displayColor || DEFAULT_CATEGORY_COLOR,
        materials: [],
      };
    if (!byCategory[key].color && m?.displayColor)
      byCategory[key].color = m.displayColor;
    byCategory[key].materials.push({
      referenceName: m.referenceName,
      displayColor: m?.displayColor,
      examples: Array.isArray(m.examples) ? m.examples : [],
    });
  }

  const groups = Object.entries(byCategory).map(
    ([parentCategory, { color, materials }]) => ({
      id: parentCategory,
      parentCategory,
      color: color || DEFAULT_CATEGORY_COLOR,
      materials,
    }),
  );

  groups.sort((a, b) => {
    const orderA = PARENT_CATEGORY_ORDER[a.parentCategory] ?? 99;
    const orderB = PARENT_CATEGORY_ORDER[b.parentCategory] ?? 99;
    return orderA - orderB;
  });

  return groups;
}

/**
 * Onglet "Matériaux non autorisés" – liste dynamique à partir du flux (authorizedMaterial).
 */
const ZoneSettingsMaterials = ({ container, onModifyFlow }) => {
  const { t } = useTranslation();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const hasFlow = Boolean(
    container?.contentCategory ?? container?.contentCategoryJoin?._id,
  );
  const flowId =
    container?.contentCategory ?? container?.contentCategoryJoin?._id;
  const unauthorizedGroups = useMemo(
    () => (hasFlow ? buildUnauthorizedGroups(container) : []),
    [container, hasFlow],
  );

  return (
    <div className="zone-settings__materials w-full flex flex-col gap-6">
      {/* Bannière – fond clair, icône, texte, bouton Modifier le flux, fermer (x) */}
      {!bannerDismissed && (
        <div className="zone-settings__materials-banner relative">
          <button
            type="button"
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-[#374151] hover:text-[#111] rounded transition-colors"
            onClick={() => setBannerDismissed(true)}
            aria-label={t("Close")}
          >
            <span className="text-xl leading-none">×</span>
          </button>
          <div className="zone-settings__materials-banner-inner pr-10 max-md:flex-col max-md:items-start">
            <div className="zone-settings__materials-banner-icon">
              <PlasticBinIcon className="w-full h-auto" aria-hidden />
            </div>
            <div className="zone-settings__materials-banner-content">
              <h3 className="zone-settings__materials-banner-title">
                {t("containerSettings.materialsBannerTitle")}
              </h3>
              <p className="zone-settings__materials-banner-text">
                <Trans
                  i18nKey="containerSettings.materialsBannerText1"
                  values={{
                    wasteName:
                      container?.wasteName ??
                      container?.contentCategoryJoin?.name ??
                      "—",
                  }}
                  components={{ 1: <strong className="font-semibold" /> }}
                />
              </p>
              <p className="zone-settings__materials-banner-text">
                {t("containerSettings.materialsBannerText2")}
              </p>
              {flowId ? (
                <button
                  type="button"
                  onClick={onModifyFlow}
                  className="zone-settings__materials-banner-btn !text-white"
                >
                  {t("containerSettings.modifyFlow")}
                  <OpenInNewIcon className="w-3 h-3 shrink-0" aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onModifyFlow}
                  className="zone-settings__materials-banner-btn !text-white"
                >
                  {t("Add a new material stream")}
                  <OpenInNewIcon className="w-3 h-3 shrink-0" aria-hidden />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!hasFlow && (
        <p className="text-neutral-500 text-sm">
          {t("containerSettings.noFlowForUnauthorizedMaterials")}
        </p>
      )}

      {hasFlow && unauthorizedGroups.length === 0 && (
        <p className="text-neutral-500 text-sm">
          {t("containerSettings.noUnauthorizedMaterials")}
        </p>
      )}

      <div className="space-y-4">
        {unauthorizedGroups.map((group) => {
          const CategoryIcon = getCategoryIconComponent(group.parentCategory);
          const pluralKey = `${group.parentCategory}_plural`;
          const categoryLabel =
            t(pluralKey) !== pluralKey
              ? t(pluralKey)
              : t(group.parentCategory, { defaultValue: group.parentCategory });
          return (
            <div key={group.id} className="zone-settings__materials-category">
              <div className="zone-settings__materials-category-title">
                <CategoryIcon />
                <span className="font-extrabold text-2xl leading-8">
                  {categoryLabel !== group.parentCategory
                    ? capitalizeFirstLetter(categoryLabel)
                    : group.parentCategory}
                </span>
              </div>
              <ul className="zone-settings__materials-list">
                {group.materials.map((mat) => (
                  <li
                    key={mat.referenceName}
                    className="zone-settings__materials-row"
                  >
                    <span
                      className="zone-settings__materials-row-dot"
                      style={{
                        backgroundColor: mat.displayColor || group.color,
                      }}
                      aria-hidden
                    />
                    <span className="zone-settings__materials-row-name">
                      {t(mat.referenceName) !== mat.referenceName
                        ? t(mat.referenceName)
                        : mat.referenceName}
                    </span>
                    <button
                      type="button"
                      className={`zone-settings__materials-examples ${!mat.examples?.length ? "zone-settings__materials-examples--empty" : ""}`}
                      title={
                        mat.examples?.length
                          ? mat.examples.join(", ")
                          : undefined
                      }
                      disabled={!mat.examples?.length}
                      aria-disabled={!mat.examples?.length}
                    >
                      <ImageIcon
                        className="zone-settings__materials-examples-icon w-4 h-4"
                        aria-hidden
                      />
                      <span className="zone-settings__materials-examples-text">
                        {t("containerSettings.examples")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ZoneSettingsMaterials;
